import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { addBookmark, fetchBookmarks, removeBookmark, type BookmarkRecord } from '../api';
import { useAuth } from '../auth/useAuth';
import { postTypeOrNews, topicOrOther } from '../categorize';
import { reportNonFatal } from '../observability/reportNonFatal';
import type { Category, Impact, ParsedItem, UpdateType } from '../types';

// A toggle recorded during the initial load: the optimistic record for an
// add, null for a remove. Wrapped so each toggle's entry has its own identity.
type LoadOverride = { record: BookmarkRecord | null };

export function useBookmarks() {
  const { account, acquireApiToken } = useAuth();
  const [records, setRecords] = useState<BookmarkRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Hold acquireApiToken in a ref so effects + callbacks can stay stable.
  // Otherwise the callback's ref churns each time the Teams-SSO probe settles,
  // refiring this effect and producing a duplicate /api/bookmarks fetch.
  const acquireApiTokenRef = useRef(acquireApiToken);
  acquireApiTokenRef.current = acquireApiToken;

  // While a GET is in flight: id → state the user asked for since the GET
  // started (the optimistic record for an add, null for a remove). The GET may
  // have been served before those writes landed, so its list is merged with
  // these rather than trusted outright. null when no load is in progress.
  // Same approach as useMutes.
  const loadOverridesRef = useRef<Map<string, LoadOverride> | null>(null);

  useEffect(() => {
    if (!account) {
      setRecords([]);
      return;
    }
    let cancelled = false;
    const overrides = new Map<string, LoadOverride>();
    loadOverridesRef.current = overrides;
    // Only this load's own map may be cleared — a newer load owns the ref
    // once this one is cancelled.
    const release = () => {
      if (loadOverridesRef.current === overrides) loadOverridesRef.current = null;
    };
    setLoading(true);
    void (async () => {
      try {
        const token = await acquireApiTokenRef.current();
        const list = await fetchBookmarks(token);
        if (cancelled) return;
        release();
        setRecords(mergeOverrides(list, overrides));
        setError(null);
      } catch (err) {
        if (cancelled) return;
        release();
        setError(err instanceof Error ? err.message : String(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
      release();
    };
  }, [account]);

  // Memoise so `toggle`'s useCallback identity is stable between renders —
  // otherwise any consumer memoising on it (e.g. Dashboard's filter memo)
  // would invalidate on every render.
  const ids = useMemo(() => new Set(records.map((r) => r.itemId)), [records]);

  const toggle = useCallback(
    async (item: ParsedItem): Promise<void> => {
      const currentlyBookmarked = ids.has(item.id);
      const optimistic: BookmarkRecord | null = currentlyBookmarked
        ? null
        : {
            itemId: item.id,
            title: item.title,
            summaryFi: item.summaryFI,
            sourceUrl: item.sourceURL,
            publishedDate: item.publishedDate,
            category: item.category,
            impact: item.impact,
            sourceFeed: item.sourceFeed,
            subcategory: item.subcategory ?? '',
            msTag: item.msTag ?? '',
            updateType: item.updateType,
            actionNeeded: item.actionNeeded,
            // Same community snapshot fields addBookmark sends.
            ...(item.kind === 'community'
              ? {
                  kind: item.kind,
                  topic: item.topic,
                  postType: item.postType,
                  author: item.author ?? undefined,
                }
              : {}),
            bookmarkedAt: new Date().toISOString(),
          };
      // Remember the intent if the initial GET is still loading. Captured once
      // so a failure only clears the entry from the load it was recorded in.
      // Each toggle writes its own entry object, so a failure can tell whether
      // the entry is still its own (useBookmarks has no per-id pending guard,
      // so a later toggle on the same id may have overwritten it meanwhile).
      const overrides = loadOverridesRef.current;
      const previousOverride = overrides?.get(item.id);
      const ownOverride: LoadOverride = { record: optimistic };
      overrides?.set(item.id, ownOverride);
      // Optimistic flip, revert on failure.
      if (optimistic === null) {
        setRecords((prev) => prev.filter((r) => r.itemId !== item.id));
      } else {
        setRecords((prev) => [...prev, optimistic]);
      }
      try {
        const token = await acquireApiTokenRef.current();
        if (currentlyBookmarked) await removeBookmark(token, item.id);
        else await addBookmark(token, item);
      } catch (err) {
        // Server state is unchanged by this toggle: restore whatever an earlier
        // (successful) toggle during the same load recorded, if anything —
        // unless a newer toggle has since recorded its own intent.
        if (overrides?.get(item.id) === ownOverride) {
          if (previousOverride === undefined) overrides.delete(item.id);
          else overrides.set(item.id, previousOverride);
        }
        // Revert by re-fetching the canonical list.
        try {
          const token = await acquireApiTokenRef.current();
          setRecords(await fetchBookmarks(token));
        } catch (revertErr) {
          // Double failure — the primary op failed AND the revert fetch
          // failed. User's bookmark state is now whatever the optimistic
          // mutation left it as; the exposed `error` is the primary op's
          // message. Beacon the revert failure so App Insights can see it.
          reportNonFatal(revertErr, 'useBookmarks.revert-fetch', {
            primary: err instanceof Error ? err.message : String(err),
          });
        }
        setError(err instanceof Error ? err.message : String(err));
      }
    },
    [ids],
  );

  const isBookmarked = useCallback((itemId: string) => ids.has(itemId), [ids]);

  // Same reason as `ids`: Dashboard's filter memo depends on `asItems`, so
  // both the function and the array it returns only change with `records`.
  const items = useMemo(() => records.map(bookmarkToItem), [records]);
  const asItems = useCallback(() => items, [items]);

  return {
    isBookmarked,
    toggle,
    loading,
    error,
    count: records.length,
    records,
    asItems,
  };
}

// Apply toggles recorded during a load onto the list that load returned:
// added ids get the optimistic record (keeping the server's record when
// present), removed ids are dropped.
function mergeOverrides(
  list: BookmarkRecord[],
  overrides: Map<string, LoadOverride>,
): BookmarkRecord[] {
  if (overrides.size === 0) return list;
  const merged = list.filter((r) => overrides.get(r.itemId)?.record !== null);
  const present = new Set(merged.map((r) => r.itemId));
  for (const { record } of overrides.values()) {
    if (record && !present.has(record.itemId)) merged.push(record);
  }
  return merged;
}

// Convert a stored bookmark snapshot back into the ParsedItem shape so it can
// be rendered through the existing UpdateCard / kanban code paths. Older
// bookmarks (saved before snapshot fields were extended) get sensible defaults.
// Only kind 'community' marks a community bookmark; a missing or other kind
// is official, so old bookmarks stay in the official views.
function bookmarkToItem(b: BookmarkRecord): ParsedItem {
  const community = b.kind === 'community';
  return {
    ...(community
      ? {
          kind: 'community' as const,
          topic: topicOrOther(b.topic),
          postType: postTypeOrNews(b.postType),
          author: b.author || b.sourceFeed || null,
        }
      : {}),
    id: b.itemId,
    title: b.title,
    summaryFI: b.summaryFi,
    publishedDate: b.publishedDate,
    sourceURL: b.sourceUrl,
    sourceFeed: b.sourceFeed || '—',
    category: (b.category as Category) || 'azure-ai',
    subcategory: b.subcategory || null,
    msTag: b.msTag || null,
    impact: (b.impact as Impact) || 'medium',
    updateType: (b.updateType as UpdateType) || 'update',
    actionNeeded: !!b.actionNeeded,
    effectiveDate: b.publishedDate || null,
  };
}
